// The personal habit log for one day: `habitLogs/{userId}_{habitId}_{yyyy-MM-dd}`.
//
// SHARED FILE: this module is copied byte-for-byte into namat (web) and
// namat-mobile at src/lib/habits/habitLogState.ts. Change both copies
// together (namat#36 P1-1). It is pure on purpose: no Firestore, no clock, no
// date library, so both clients compute the SAME document for the same tap.
//
// The schema is the web's original one: a habit asked for N times a day
// (frequencyConfig.timesPerFrequency) keeps a running `completionCount` and is
// only `completed` once that count reaches `targetCount`. Unlogging takes one
// away again, and the document is removed only when the last one goes.

export type HabitLogAction =
  /** One more completion today (a tap on "log"). */
  | 'log'
  /** One fewer completion today (a tap on "unlog"). */
  | 'unlog'
  /** Straight to the target in one write (step-driven auto-completion). */
  | 'complete';

/** The fields of today's existing log that decide the next state. */
export interface ExistingHabitLog {
  completed?: boolean;
  completionCount?: number;
}

export interface HabitLogContext {
  userId: string;
  habitId: string;
  /** The log's day, `yyyy-MM-dd` (see localDateKey). */
  date: string;
  /** Completions needed for the day to count (see targetCountOf). */
  targetCount: number;
  /** Consecutive completed days ending YESTERDAY (see streakBefore). */
  priorStreak: number;
  /** Whole days since the habit was created (see daysSinceStart). */
  daysSinceStart: number;
  now: Date;
  habitName?: string;
  habitCategory?: string;
  /** frequencyConfig.frequency; 'daily' when the habit has none. */
  frequencyType?: string;
  /** Who wrote the log. 'manual' for a tap. */
  source?: string;
}

export type HabitLogTransition =
  /** Write the whole document (setDoc, no merge). */
  | { type: 'set'; data: Record<string, unknown> }
  /** Patch the existing document (updateDoc). */
  | { type: 'update'; data: { completionCount: number; completed: boolean; timestamp: Date } }
  /** Remove the document. */
  | { type: 'delete' }
  /** Nothing to write; the caller throws `new Error(reason)` as before. */
  | { type: 'reject'; reason: 'already_logged' | 'not_logged_today' };

export function habitLogId(userId: string, habitId: string, date: string): string {
  return `${userId}_${habitId}_${date}`;
}

/** Local calendar day as `yyyy-MM-dd` — what date-fns `format(d, 'yyyy-MM-dd')` gives. */
export function localDateKey(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/**
 * Completions already recorded on a log. A legacy log (before counts, or
 * written by the old mobile path) carries only `completed: true` and counts
 * as one.
 */
export function completionCountOf(log: ExistingHabitLog | null | undefined): number {
  if (!log) return 0;
  return log.completionCount || (log.completed ? 1 : 0);
}

/** Completions per day the habit asks for; never below 1. */
export function targetCountOf(habit: { frequencyConfig?: { timesPerFrequency?: number } | null } | null | undefined): number {
  const raw = habit?.frequencyConfig?.timesPerFrequency;
  return typeof raw === 'number' && raw >= 1 ? Math.floor(raw) : 1;
}

/** Whole days from the habit's creation day (local midnight) to `now`. */
export function daysSinceStart(createdAt: Date, now: Date): number {
  const start = new Date(createdAt.getTime());
  start.setHours(0, 0, 0, 0);
  return Math.floor((now.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));
}

/** Consecutive completed days ending the day before `today`, looking back at most `maxDays`. */
export function streakBefore(
  logs: ReadonlyArray<{ date?: string; completed?: boolean }>,
  today: Date,
  maxDays: number,
): number {
  const completedDays = new Set(logs.filter((l) => l && l.completed && l.date).map((l) => l.date as string));
  let streak = 0;
  for (let i = 1; i <= maxDays; i++) {
    const day = new Date(today.getTime());
    day.setDate(day.getDate() - i);
    if (!completedDays.has(localDateKey(day))) break;
    streak++;
  }
  return streak;
}

function fullDocument(ctx: HabitLogContext, completionCount: number): Record<string, unknown> {
  const completed = completionCount >= ctx.targetCount;
  const data: Record<string, unknown> = {
    userId: ctx.userId,
    habitId: ctx.habitId,
    date: ctx.date,
    completed,
    completionCount,
    targetCount: ctx.targetCount,
    source: ctx.source || 'manual',
    timestamp: ctx.now,
    streakLength: completed ? ctx.priorStreak + 1 : 0,
    daysSinceStart: ctx.daysSinceStart,
    metadata: {
      loggedFromApp: true,
      frequencyType: ctx.frequencyType || 'daily',
    },
  };
  // Firestore rejects a literal undefined (mobile does not set
  // ignoreUndefinedProperties), so optional fields are omitted, not undefined.
  if (ctx.habitCategory !== undefined) data.habitCategory = ctx.habitCategory;
  if (ctx.habitName !== undefined) data.habitName = ctx.habitName;
  return data;
}

/** What to do with today's log for one action. `existing` is null when there is no log yet. */
export function nextHabitLogState(
  existing: ExistingHabitLog | null | undefined,
  action: HabitLogAction,
  ctx: HabitLogContext,
): HabitLogTransition {
  if (action === 'unlog') {
    if (!existing) return { type: 'reject', reason: 'not_logged_today' };
    const current = existing.completionCount || 1;
    if (current <= 1) return { type: 'delete' };
    const completionCount = current - 1;
    return {
      type: 'update',
      data: { completionCount, completed: completionCount >= ctx.targetCount, timestamp: ctx.now },
    };
  }

  const current = completionCountOf(existing);
  if (current >= ctx.targetCount) return { type: 'reject', reason: 'already_logged' };
  const completionCount = action === 'complete' ? ctx.targetCount : current + 1;
  return { type: 'set', data: fullDocument(ctx, completionCount) };
}
